import { useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { MyCoachContinuityStore } from '@/lib/myCoachContinuity';
import { MyCoachContinuityContext, useContinuityUnloadWarning } from '@/hooks/useMyCoachContinuity';

export function MyCoachContinuityProvider({ actorId, children }: { actorId: string | null; children: ReactNode }) {
  const context = useMemo(() => ({ actorId, store: new MyCoachContinuityStore() }), [actorId]);
  useEffect(() => { context.store.activate(); return context.store.revoke; }, [context]);
  useContinuityUnloadWarning(context.store);
  return <MyCoachContinuityContext.Provider value={context}>{children}</MyCoachContinuityContext.Provider>;
}

/** Persist through route unmounts, but rotate all private memory on logout/actor change. */
export function MyCoachContinuityBoundary({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  return <MyCoachContinuityProvider actorId={user?.id ?? null}>{children}</MyCoachContinuityProvider>;
}

/** Standalone embeds keep child-dialog drafts for the lifetime of their parent. */
export function MyCoachContinuityScope({ children }: { children: ReactNode }) {
  const inherited=useContext(MyCoachContinuityContext);
  const local=useMemo(()=>({actorId:'mounted',store:new MyCoachContinuityStore(),local:true}),[]);
  useEffect(()=>{if(inherited)return;local.store.activate();return local.store.revoke;},[inherited,local]);
  useContinuityUnloadWarning(local.store,!inherited);
  return <MyCoachContinuityContext.Provider value={inherited??local}>{children}</MyCoachContinuityContext.Provider>;
}
